import React from 'react';
import {
    List,
    Datagrid,
    TextField,
    NumberField,
    DateField,
    BooleanField,
    FunctionField,
    EditButton,
    TextInput,
    SelectInput,
    BooleanInput,
    TopToolbar,
    CreateButton,
    ExportButton,
    RefreshButton,
    FilterButton,
    useTranslate,
    ReferenceField,
    useListContext,
    RecordContextProvider,
} from 'react-admin';
import {
    Chip,
    Box,
    LinearProgress,
    Typography,
    useMediaQuery,
    useTheme,
    Fab,
} from '@mui/material';
import {
    Bolt,
    CheckCircle,
    Cancel,
    Warning,
    Add as AddIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const useLoadGroupsFilters = () => {
    const translate = useTranslate();

    return [
        <TextInput
            key="search"
            label={translate("resources.load-groups.list.filters.search")}
            source="search"
            alwaysOn
        />,
        <SelectInput
            key="status"
            source="status"
            label={translate("resources.load-groups.fields.status")}
            choices={[
                { id: 'active', name: translate("resources.load-groups.statuses.active") },
                { id: 'inactive', name: translate("resources.load-groups.statuses.inactive") },
                { id: 'maintenance', name: translate("resources.load-groups.statuses.maintenance") },
            ]}
        />,
        <SelectInput
            key="phase"
            source="phase"
            label={translate("resources.load-groups.fields.phase")}
            choices={[
                { id: 'single', name: translate("resources.load-groups.phases.single") },
                { id: 'three', name: translate("resources.load-groups.phases.three") },
            ]}
        />,
        <BooleanInput
            key="loadBalancingEnabled"
            label={translate("resources.load-groups.fields.loadBalancingEnabled")}
            source="loadBalancingEnabled"
        />,
    ];
};

const ListActions = () => (
    <TopToolbar>
        <FilterButton />
        <CreateButton />
        <ExportButton />
        <RefreshButton />
    </TopToolbar>
);

const MobileListActions = () => (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 0 }}>
        <FilterButton />
        <ExportButton />
        <RefreshButton />
    </Box>
);

const PowerDisplay = ({ record }: any) => {
    const translate = useTranslate();
    if (!record) return null;

    const utilizationPercent = record.chargerStats?.powerUtilizationPercent || 0;
    const currentPower = record.currentPower || 0;
    const maxPower = record.maxPower || 0;

    return (
        <Box>
            <Typography variant="body2">
                {currentPower.toFixed(1)} / {maxPower} kW
            </Typography>
            <LinearProgress
                variant="determinate"
                value={utilizationPercent}
                sx={{
                    mt: 0.5,
                    backgroundColor: '#E0E0E0',
                    '& .MuiLinearProgress-bar': {
                        backgroundColor: utilizationPercent > 80 ? '#FF6B35' : '#009A75',
                    }
                }}
            />
            <Typography variant="caption" color="text.secondary">
                {utilizationPercent}% {translate("resources.load-groups.fields.utilized")}
            </Typography>
        </Box>
    );
};

const ChargerStatsDisplay = ({ record }: any) => {
    const translate = useTranslate();
    if (!record || !record.chargerStats) return null;

    const { totalChargers, activeChargers } = record.chargerStats;

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Bolt sx={{ fontSize: 20, color: '#009A75' }} />
            <Typography variant="body2">
                {activeChargers} / {totalChargers}
            </Typography>
        </Box>
    );
};

const StatusDisplay = ({ record }: any) => {
    const translate = useTranslate();
    if (!record) return null;

    const statusConfig = {
        active: { color: '#00C853', icon: <CheckCircle sx={{ fontSize: 16 }} />, label: translate("resources.load-groups.statuses.active") },
        inactive: { color: '#FF6B35', icon: <Cancel sx={{ fontSize: 16 }} />, label: translate("resources.load-groups.statuses.inactive") },
        maintenance: { color: '#FFA726', icon: <Warning sx={{ fontSize: 16 }} />, label: translate("resources.load-groups.statuses.maintenance") },
    };

    const config = statusConfig[record.status as keyof typeof statusConfig];

    return (
        <Chip
            icon={config?.icon}
            label={config?.label || record.status}
            size="small"
            sx={{
                backgroundColor: config?.color + '20',
                color: config?.color,
                fontWeight: 500,
                '& .MuiChip-icon': {
                    color: config?.color,
                },
            }}
        />
    );
};

const PriorityDisplay = ({ record }: any) => {
    if (!record) return null;

    const getPriorityColor = (level: number) => {
        if (level <= 3) return '#009A75';
        if (level <= 7) return '#FFA726';
        return '#FF6B35';
    };

    return (
        <Chip
            label={`P${record.priorityLevel}`}
            size="small"
            sx={{
                backgroundColor: getPriorityColor(record.priorityLevel) + '20',
                color: getPriorityColor(record.priorityLevel),
                fontWeight: 600,
                minWidth: 40,
            }}
        />
    );
};

const MobileLoadGroupList = () => {
    const { data, isLoading } = useListContext();
    const navigate = useNavigate();
    const translate = useTranslate();
    if (isLoading || !data) return null;
    return (
        <Box>
            {data.map((record: any) => (
                <RecordContextProvider key={record.id} value={record}>
                    <Box
                        onClick={() => navigate(`/load-groups/${record.id}`)}
                        sx={{
                            py: 1.5, px: 2,
                            borderBottom: '1px solid #F0F0F0',
                            cursor: 'pointer',
                            '&:active': { backgroundColor: '#F5F5F5' },
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, mr: 1 }}>
                                {record.name}
                            </Typography>
                            <StatusDisplay record={record} />
                        </Box>
                        <ReferenceField source="siteId" reference="sites" link={false}>
                            <FunctionField render={(site: any) => (
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                                    {site?.name}
                                </Typography>
                            )} />
                        </ReferenceField>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                    {(record.currentPower || 0).toFixed(1)}/{record.maxPower || 0} kW
                                </Typography>
                                <LinearProgress
                                    variant="determinate"
                                    value={record.chargerStats?.powerUtilizationPercent || 0}
                                    sx={{
                                        height: 4, borderRadius: 2,
                                        backgroundColor: '#E0E0E0',
                                        '& .MuiLinearProgress-bar': {
                                            backgroundColor: (record.chargerStats?.powerUtilizationPercent || 0) > 80 ? '#FF6B35' : '#009A75',
                                            borderRadius: 2,
                                        }
                                    }}
                                />
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Bolt sx={{ fontSize: 14, color: '#009A75' }} />
                                <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                                    {record.chargerStats?.activeChargers || 0}/{record.chargerStats?.totalChargers || 0}
                                </Typography>
                            </Box>
                            <PriorityDisplay record={record} />
                        </Box>
                    </Box>
                </RecordContextProvider>
            ))}
        </Box>
    );
};

export const LoadGroupsList = () => {
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();
    const loadGroupsFilters = useLoadGroupsFilters();

    return (
        <Box sx={{ position: 'relative' }}>
            <List
                filters={loadGroupsFilters}
                actions={isMobile ? <MobileListActions /> : <ListActions />}
                empty={isMobile ? false : undefined}
                sort={{ field: 'name', order: 'ASC' }}
                perPage={25}
                title={translate("resources.load-groups.name")}
                sx={{
                    '& .RaList-content': {
                        backgroundColor: isMobile ? 'transparent' : '#FAFAFA',
                    },
                    ...(isMobile && {
                        '& .RaList-content': {
                            backgroundColor: 'transparent !important',
                            boxShadow: 'none !important',
                            mt: 2,
                        },
                        '& .RaList-actions': {
                            backgroundColor: 'transparent !important',
                            p: '0 !important',
                            minHeight: 'auto !important',
                        },
                        '& .RaListToolbar-root': {
                            backgroundColor: 'transparent !important',
                            flexDirection: 'row !important',
                            flexWrap: 'wrap !important',
                            alignItems: 'center !important',
                            gap: '8px !important',
                        },
                        '& .RaListToolbar-root > span': { display: 'none' },
                        '& .RaListToolbar-root > form, & .RaFilterForm-root': {
                            flex: '1 1 100% !important',
                            width: '100% !important',
                        },
                        '& .filter-field, & .RaFilterForm-filterFormInput': {
                            width: '100% !important',
                        },
                        '& .filter-field .MuiFormControl-root': { width: '100% !important' },
                        '& .filter-field .MuiInputBase-root': { width: '100% !important' },
                        '& .MuiToolbar-root': {
                            backgroundColor: 'transparent !important',
                            minHeight: 'auto !important',
                        },
                    }),
                }}
            >
                {isMobile ? <MobileLoadGroupList /> : (
                    <Datagrid
                        rowClick="edit"
                        bulkActionButtons={false}
                        sx={{
                            '& .RaDatagrid-headerCell': {
                                backgroundColor: '#F5F5F5',
                                fontWeight: 'bold',
                            },
                            '& .RaDatagrid-row': {
                                '&:hover': {
                                    backgroundColor: '#F0F7FF',
                                },
                            },
                        }}
                    >
                        <TextField
                            source="name"
                            label={translate("resources.load-groups.fields.name")}
                        />

                        <ReferenceField
                            source="siteId"
                            reference="sites"
                            label={translate("resources.load-groups.fields.site")}
                        >
                            <TextField source="name" />
                        </ReferenceField>

                        <FunctionField
                            label={translate("resources.load-groups.fields.powerUsage")}
                            render={(record: any) => <PowerDisplay record={record} />}
                        />

                        <FunctionField
                            label={translate("resources.load-groups.fields.chargers")}
                            render={(record: any) => <ChargerStatsDisplay record={record} />}
                        />

                        <FunctionField
                            label={translate("resources.load-groups.fields.phase")}
                            render={(record: any) => (
                                <Chip
                                    label={translate(`resources.load-groups.phases.${record.phase}`)}
                                    size="small"
                                    variant="outlined"
                                />
                            )}
                        />

                        <FunctionField
                            source="voltage"
                            label={translate("resources.load-groups.fields.voltage")}
                            render={(record: any) => `${record.voltage} V`}
                        />

                        <FunctionField
                            label={translate("resources.load-groups.fields.priorityLevel")}
                            render={(record: any) => <PriorityDisplay record={record} />}
                        />

                        <BooleanField
                            source="loadBalancingEnabled"
                            label={translate("resources.load-groups.fields.loadBalancing")}
                        />

                        <FunctionField
                            label={translate("resources.load-groups.fields.status")}
                            render={(record: any) => <StatusDisplay record={record} />}
                        />

                        <DateField
                            source="updatedAt"
                            label={translate("resources.load-groups.fields.updatedAt")}
                            showTime
                        />

                        <EditButton />
                    </Datagrid>
                )}
            </List>
            {isMobile && (
                <Fab
                    color="primary"
                    onClick={() => navigate('/load-groups/create')}
                    sx={{
                        position: 'fixed',
                        bottom: 24,
                        right: 24,
                        backgroundColor: '#009A75',
                        '&:hover': { backgroundColor: '#007A5E' },
                        zIndex: 1100,
                    }}
                >
                    <AddIcon />
                </Fab>
            )}
        </Box>
    );
};
